import {
  defaultWaybillSettings,
  type DefaultWaybillSettings,
  type OrgWaybillLogoBackground,
  type OrgWaybillLogoRounding,
  type OrgWaybillLogoShape,
  type OrgWaybillLogoSize,
  type OrgWaybillSettings,
} from '@hero/schema';

/** Fully-resolved waybill branding values (platform defaults applied). */
export type ResolvedWaybillSettings = {
  accent_color: string;
  use_org_logo: boolean;
  logo_shape: OrgWaybillLogoShape;
  logo_background: OrgWaybillLogoBackground;
  logo_size: OrgWaybillLogoSize;
  logo_rounding: OrgWaybillLogoRounding;
};

/** Platform defaults for `Org.settings.waybill_settings` — single source for render + reset. */
export function getDefaultWaybillSettings(): ResolvedWaybillSettings {
  return { ...defaultWaybillSettings };
}

/** Merge sparse persisted settings with platform defaults for display and render. */
export function resolveWaybillSettings(
  saved?: OrgWaybillSettings | null,
): ResolvedWaybillSettings {
  return {
    accent_color: saved?.accent_color ?? defaultWaybillSettings.accent_color,
    use_org_logo: saved?.use_org_logo ?? defaultWaybillSettings.use_org_logo,
    logo_shape: saved?.logo_shape ?? defaultWaybillSettings.logo_shape,
    logo_background: saved?.logo_background ?? defaultWaybillSettings.logo_background,
    logo_size: saved?.logo_size ?? defaultWaybillSettings.logo_size,
    logo_rounding: saved?.logo_rounding ?? defaultWaybillSettings.logo_rounding,
  };
}

/**
 * When every value matches platform defaults, returns `null` (no `waybill_settings` doc).
 * When the logo is enabled, returns the full branding snapshot (accent + all logo fields).
 * When the logo is disabled, persists only non-logo customizations (e.g. accent color).
 */
export function compactWaybillSettings(
  values: OrgWaybillSettings,
): OrgWaybillSettings | null {
  const resolved = resolveWaybillSettings(values);

  const hasCustom =
    resolved.accent_color !== defaultWaybillSettings.accent_color ||
    resolved.use_org_logo !== defaultWaybillSettings.use_org_logo ||
    resolved.logo_shape !== defaultWaybillSettings.logo_shape ||
    resolved.logo_background !== defaultWaybillSettings.logo_background ||
    resolved.logo_size !== defaultWaybillSettings.logo_size ||
    resolved.logo_rounding !== defaultWaybillSettings.logo_rounding;

  if (!hasCustom) return null;

  if (resolved.use_org_logo) {
    return {
      accent_color: resolved.accent_color,
      use_org_logo: true,
      logo_shape: resolved.logo_shape,
      logo_background: resolved.logo_background,
      logo_size: resolved.logo_size,
      logo_rounding: resolved.logo_rounding,
    };
  }

  const compact: OrgWaybillSettings = {};
  if (resolved.accent_color !== defaultWaybillSettings.accent_color) {
    compact.accent_color = resolved.accent_color;
  }

  return Object.keys(compact).length > 0 ? compact : null;
}

/** True when the org has no custom waybill overrides stored. */
export function hasCustomWaybillSettings(saved?: OrgWaybillSettings | null): boolean {
  if (!saved) return false;
  return compactWaybillSettings(resolveWaybillSettings(saved)) !== null;
}

export type { DefaultWaybillSettings };